import { message } from 'antd';
import { RcFile } from 'antd/es/upload';
import { FILE_UPLOAD_SIZE_LIMIT } from './constants';
import millify from 'millify';

const validFileTypes = [
  'text/plain',
  'text/rtf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/pdf',
  'text/csv',
  'image/jpeg',
  'image/png',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/x-iwork-keynote-sffkey',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/x-iwork-numbers-sffnumbers',
  'application/zip',
  'application/x-zip-compressed',
  // Email formats. Both are viewable in the app, so both are uploadable.
  'application/vnd.ms-outlook',
  'message/rfc822'
];

/**
 * Extensions accepted regardless of what media type the browser reported.
 *
 * ⚠️ THIS EXISTS BECAUSE A MEDIA TYPE IS NOT RELIABLE FOR THESE TWO. A browser reports
 * `application/vnd.ms-outlook` for a `.msg` only when Outlook is registered on that machine; on one
 * without it the same file arrives as `''` or `application/octet-stream`. Matching on media type
 * alone therefore accepts a file on one colleague's laptop and rejects it on another's — a
 * machine-dependent failure that a bug report cannot easily explain.
 *
 * ⛔ Keep this an ALLOWLIST of specific extensions. It must never become "allow anything whose
 * media type is empty", which would admit every unrecognised binary the browser declines to name.
 * The backend derives these formats from the extension too, so the two ends agree.
 */
const validFileExtensions = ['.msg', '.eml'];

/**
 * Reports whether the file is an accepted format, by media type or by extension.
 * @param {RcFile} file - The file being uploaded.
 * @returns {boolean} True when the format is allowed.
 */
const isAllowedFormat = (file: RcFile): boolean => {
  if (validFileTypes.includes(file.type)) return true;
  const name = file.name.toLowerCase();
  return validFileExtensions.some((extension) => name.endsWith(extension));
};

const FileUploadValidator = (_file: RcFile, uploadedFileList: any[] = [], maxFiles = 5) => {
  const isAllowedType = isAllowedFormat(_file);
  if (!isAllowedType) {
    message.error(`${_file.name} is not a Valid file type`).then();
    return false;
  }
  const isAllowedSize = _file.size < FILE_UPLOAD_SIZE_LIMIT;
  if (!isAllowedSize) {
    message.error(`${_file.name} exceeds the allowed size of ${millify(FILE_UPLOAD_SIZE_LIMIT)}`).then();
    return false;
  }
  if (_file.size === 0) {
    message.error(`${_file.name} is an empty file`).then();
    return false;
  }
  if (uploadedFileList.find((file) => file.name == _file.name)) {
    message.error(`${_file.name} is already attached.`).then();
    return false;
  }
  if (uploadedFileList.length >= maxFiles) {
    message.error(`You are allowed to upload only a maximum of ${maxFiles} files`).then();
    return false;
  }
  return true;
};

export default FileUploadValidator;
